/**
 * Service centralisé de gestion des notifications
 * @module services/NotificationService
 */

import { toast } from 'react-toastify';
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_DEFAULTS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from '../config/notificationConstants';

/**
 * Service de notification centralisé
 * Fournit une interface uniforme pour afficher des notifications dans l'application
 */
class NotificationService {
  /**
   * Affiche une notification de succès
   * @param {string} message - Message à afficher
   * @param {Object} [options] - Options supplémentaires pour la notification
   * @returns {string|number} ID de la notification
   */
  static success(message, options = {}) {
    const config = {
      ...NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.SUCCESS],
      ...options,
    };
    return toast.success(message, config);
  }

  /**
   * Affiche une notification d'erreur
   * @param {string|Error} messageOrError - Message ou objet Error
   * @param {Object} [options] - Options supplémentaires pour la notification
   * @returns {string|number} ID de la notification
   */
  static error(messageOrError, options = {}) {
    const message = this.extractMessage(messageOrError);
    const config = {
      ...NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.ERROR],
      ...options,
    };
    return toast.error(message, config);
  }

  /**
   * Affiche une notification d'information
   * @param {string} message - Message à afficher
   * @param {Object} [options] - Options supplémentaires pour la notification
   * @returns {string|number} ID de la notification
   */
  static info(message, options = {}) {
    const config = {
      ...NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.INFO],
      ...options,
    };
    return toast.info(message, config);
  }

  /**
   * Affiche une notification d'avertissement
   * @param {string} message - Message à afficher
   * @param {Object} [options] - Options supplémentaires pour la notification
   * @returns {string|number} ID de la notification
   */
  static warning(message, options = {}) {
    const config = {
      ...NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.WARNING],
      ...options,
    };
    return toast.warning(message, config);
  }

  /**
   * Affiche une notification personnalisée
   * @param {string} message - Message à afficher
   * @param {string} type - Type de notification (success, error, info, warning)
   * @param {Object} [options] - Options supplémentaires pour la notification
   * @returns {string|number} ID de la notification
   */
  static notify(message, type = NOTIFICATION_TYPES.INFO, options = {}) {
    const defaultConfig = NOTIFICATION_DEFAULTS[type] || NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.INFO];
    const config = {
      ...defaultConfig,
      ...options,
    };

    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return toast.success(message, config);
      case NOTIFICATION_TYPES.ERROR:
        return toast.error(message, config);
      case NOTIFICATION_TYPES.WARNING:
        return toast.warning(message, config);
      case NOTIFICATION_TYPES.INFO:
      default:
        return toast.info(message, config);
    }
  }

  /**
   * Extrait le message d'erreur d'un objet Error ou d'une chaîne
   * @param {string|Error} messageOrError - Message ou objet Error
   * @returns {string} Message d'erreur formaté
   */
  static extractMessage(messageOrError) {
    if (typeof messageOrError === 'string') {
      return messageOrError;
    }
    if (messageOrError instanceof Error) {
      return messageOrError.message || ERROR_MESSAGES.GENERIC;
    }
    if (messageOrError?.message) {
      return messageOrError.message;
    }
    return ERROR_MESSAGES.GENERIC;
  }

  /**
   * Gère une erreur et affiche une notification appropriée
   * @param {Error|string} error - Erreur à gérer
   * @param {string} [context] - Contexte de l'erreur (pour le logging)
   * @param {Object} [options] - Options supplémentaires
   * @returns {string} Message d'erreur formaté
   */
  static handleError(error, context = '', options = {}) {
    const message = this.extractMessage(error);

    // Ne pas afficher de toast pour les erreurs d'authentification Twitch
    // Le contenu est flouté avec un message invitant à s'authentifier
    if (
      typeof message === 'string' &&
      (message.includes('Authentification Twitch requise') ||
        message.includes('Non authentifié'))
    ) {
      return message;
    }

    // Log de l'erreur si un contexte est fourni
    if (context) {
      console.error(`[${context}] Erreur:`, error);
    } else {
      console.error('Erreur:', error);
    }

    // Afficher la notification
    this.error(message, options);

    return message;
  }

  /**
   * Affiche un message de succès standardisé
   * @param {string} [messageKey] - Clé du message standardisé (optionnel)
   * @param {string} [customMessage] - Message personnalisé (optionnel)
   * @param {Object} [options] - Options supplémentaires
   * @returns {string|number} ID de la notification
   */
  static showSuccess(messageKey = null, customMessage = null, options = {}) {
    const message = customMessage || SUCCESS_MESSAGES[messageKey] || SUCCESS_MESSAGES.SAVED;
    return this.success(message, options);
  }

  /**
   * Affiche un message d'erreur standardisé
   * @param {string} [messageKey] - Clé du message standardisé (optionnel)
   * @param {string|Error} [customMessage] - Message personnalisé ou Error (optionnel)
   * @param {Object} [options] - Options supplémentaires
   * @returns {string|number} ID de la notification
   */
  static showError(messageKey = null, customMessage = null, options = {}) {
    if (customMessage) {
      return this.error(customMessage, options);
    }
    const message = ERROR_MESSAGES[messageKey] || ERROR_MESSAGES.GENERIC;
    return this.error(message, options);
  }

  /**
   * Ferme une notification spécifique
   * @param {string|number} toastId - ID de la notification à fermer
   */
  static dismiss(toastId) {
    toast.dismiss(toastId);
  }

  /**
   * Ferme toutes les notifications
   */
  static dismissAll() {
    toast.dismiss();
  }

  /**
   * Vérifie si une notification est active
   * @param {string|number} toastId - ID de la notification
   * @returns {boolean} True si la notification est active
   */
  static isActive(toastId) {
    return toast.isActive(toastId);
  }

  /**
   * Affiche une notification de chargement
   * @param {string} message - Message à afficher
   * @param {Object} [options] - Options supplémentaires
   * @returns {string|number} ID de la notification
   */
  static loading(message, options = {}) {
    const config = {
      ...NOTIFICATION_DEFAULTS[NOTIFICATION_TYPES.INFO],
      ...options,
    };
    return toast.loading(message, config);
  }

  /**
   * Met à jour une notification existante
   * @param {string|number} toastId - ID de la notification à mettre à jour
   * @param {Object} options - Options de mise à jour (render, type, etc.)
   * @returns {void}
   */
  static update(toastId, options) {
    toast.update(toastId, options);
  }
}

export default NotificationService;
